import React from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useGetHabitStatistics } from "../../generated/hook";
import {
  HabitStatisticsClass,
  HabitCountStatisticClass,
  HabitEntryStatisticClass,
} from "../../generated/model";
import { Stack } from "@mui/material";

interface HabitCountChartProps {
  className?: string;
}

// Type guard to check if the response is successful
const isSuccessfulResponse = (data: any): data is HabitStatisticsClass => {
  return (
    data !== null &&
    data !== undefined &&
    typeof data === "object" &&
    "habitCountOverTime" in data &&
    Array.isArray(data.habitCountOverTime) &&
    "habitEntryOverTime" in data &&
    Array.isArray(data.habitEntryOverTime)
  );
};

export const HabitCountChart: React.FC<HabitCountChartProps> = ({
  className,
}) => {
  const { data: statisticsData, isLoading, error } = useGetHabitStatistics();

  if (isLoading) {
    return (
      <div className={`p-4 ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 ${className}`}>
        <div className="text-red-500 text-center">
          Erreur lors du chargement des statistiques
        </div>
      </div>
    );
  }

  if (
    !statisticsData ||
    !isSuccessfulResponse(statisticsData) ||
    (statisticsData.habitCountOverTime.length === 0 &&
      statisticsData.habitEntryOverTime.length === 0)
  ) {
    return (
      <div className={`p-4 ${className}`}>
        <h3 className="text-lg font-semibold mb-4">Évolution des habitudes</h3>
        <div className="text-gray-500 text-center py-8">
          Aucune donnée disponible. Créez votre première habitude pour voir
          l'évolution !
        </div>
      </div>
    );
  }

  // Créer une map pour combiner les données par date
  const dateMap = new Map<
    string,
    {
      date: string;
      habitCount: number;
      entryCount: number;
      activeHabitCount: number;
      fullDate: string;
    }
  >();

  // Ajouter les données de compte d'habitudes
  statisticsData.habitCountOverTime.forEach(
    (item: HabitCountStatisticClass) => {
      const date = new Date(item.date).toLocaleDateString("fr-FR", {
        month: "short",
        day: "numeric",
      });
      dateMap.set(item.date, {
        date,
        habitCount: item.habitCount,
        entryCount: 0,
        activeHabitCount: 0,
        fullDate: item.date,
      });
    }
  );

  // Ajouter les données d'entrées d'habitudes
  statisticsData.habitEntryOverTime.forEach(
    (item: HabitEntryStatisticClass) => {
      const date = new Date(item.date).toLocaleDateString("fr-FR", {
        month: "short",
        day: "numeric",
      });
      const existing = dateMap.get(item.date);
      if (existing) {
        existing.entryCount = item.entryCount;
        existing.activeHabitCount = item.activeHabitCount;
      } else {
        dateMap.set(item.date, {
          date,
          habitCount: 0,
          entryCount: item.entryCount,
          activeHabitCount: item.activeHabitCount,
          fullDate: item.date,
        });
      }
    }
  );

  // Convertir en tableau et trier par date
  const chartData = Array.from(dateMap.values()).sort(
    (a, b) => new Date(a.fullDate).getTime() - new Date(b.fullDate).getTime()
  );

  return (
    <div className={`p-4 ${className}`}>
      <h3 className="text-lg font-semibold mb-4">
        Évolution des habitudes et des entrées
      </h3>
      <Stack sx={{ width: "100%", height: 300 }} spacing={2}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="date"
              stroke="#666"
              fontSize={12}
              tick={{ fontSize: 12 }}
            />
            <YAxis
              stroke="#666"
              fontSize={12}
              tick={{ fontSize: 12 }}
              allowDecimals={false}
              domain={[0, "dataMax"]}
            />
            <Tooltip
              labelFormatter={(label, payload) => {
                const fullDate = payload?.[0]?.payload?.fullDate;
                return fullDate
                  ? new Date(fullDate).toLocaleDateString("fr-FR", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })
                  : label;
              }}
              formatter={(value: any, name: string, props: any) => {
                if (name === "habitCount") {
                  return [value, "Nombre d'habitudes"];
                } else if (name === "activeHabitCount") {
                  const entryCount = props.payload?.entryCount;
                  const percentage =
                    value > 0 ? Math.round((entryCount / value) * 100) : 0;
                  return [
                    `${value} (${entryCount} entrées - ${percentage}%)`,
                    "Habitudes actives",
                  ];
                }
                return [value, name];
              }}
              contentStyle={{
                backgroundColor: "white",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
            <Area
              type="monotone"
              dataKey="activeHabitCount"
              stackId="1"
              stroke="#8884d8"
              strokeWidth={2}
              fill="#8884d8"
              fillOpacity={0.6}
            />
            <Area
              type="monotone"
              dataKey="entryCount"
              stackId="2"
              stroke="#82ca9d"
              strokeWidth={2}
              fill="#82ca9d"
              fillOpacity={0.6}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Stack>
      <p className="text-sm text-gray-600 mt-2">
        Graphique empilé montrant l'évolution du nombre d'habitudes créées
        (bleu) et le nombre d'habitudes actives par jour avec entrées réalisées
        (vert). Le tooltip affiche le pourcentage d'entrées réalisées par
        rapport aux habitudes actives.
      </p>
    </div>
  );
};
